import React from 'react';
import BaseSkeleton from './base-skeleton';
import SkeletonAvatar from './skeleton-avatar';
import SkeletonText from './skeleton-text';
import { useTheme } from '../../context/theme-context';

interface SkeletonCardProps {
  variant?: 'post' | 'community' | 'user' | 'product';
  className?: string;
}

const SkeletonCard: React.FC<SkeletonCardProps> = ({ variant = 'post', className = '' }) => {
  const { isDarkMode } = useTheme();
  
  const cardBgClass = isDarkMode ? 'bg-neutral-800' : 'bg-white';

  const renderPostSkeleton = () => (
    <div className={`${cardBgClass} rounded-lg p-4 shadow-sm ${className}`}>
      {/* Header with avatar and text */}
      <div className="flex items-start gap-3">
        <SkeletonAvatar size="md" />
        <div className="flex-1">
          <SkeletonText lines={2} spacing="space-y-1" lastLineWidth="60%" />
        </div>
      </div>
      
      {/* Image placeholder */}
      <div className="mt-3">
        <BaseSkeleton variant="rounded" height="200px" className="w-full" />
      </div>
      
      {/* Content lines */}
      <div className="mt-3">
        <SkeletonText lines={3} spacing="space-y-1" />
      </div>
      
      {/* Action buttons */}
      <div className="flex gap-4 mt-4">
        <BaseSkeleton variant="rounded" width="80px" height="32px" />
        <BaseSkeleton variant="rounded" width="80px" height="32px" />
        <BaseSkeleton variant="rounded" width="80px" height="32px" />
      </div>
    </div>
  );

  const renderCommunitySkeleton = () => (
    <div className={`${cardBgClass} rounded-lg p-4 shadow-sm min-w-[200px] ${className}`}>
      <div className="flex flex-col items-center text-center">
        <SkeletonAvatar size="xl" />
        <div className="mt-3 w-full">
          <SkeletonText lines={2} spacing="space-y-2" lastLineWidth="80%" />
        </div>
        <BaseSkeleton variant="rounded" width="100px" height="32px" className="mt-3" />
      </div>
    </div>
  );

  const renderUserSkeleton = () => (
    <div className={`flex items-center gap-3 p-3 ${cardBgClass} rounded-lg ${className}`}>
      <SkeletonAvatar size="lg" />
      <div className="flex-1">
        <SkeletonText lines={2} spacing="space-y-1" />
      </div>
    </div>
  );

  const renderProductSkeleton = () => (
    <div className={`${cardBgClass} rounded-lg overflow-hidden shadow-sm ${className}`}>
      <BaseSkeleton variant="rectangular" height="200px" className="w-full" />
      <div className="p-4">
        <SkeletonText lines={2} spacing="space-y-2" />
        <div className="flex justify-between items-center mt-4">
          <BaseSkeleton variant="rounded" width="80px" height="24px" />
          <BaseSkeleton variant="rounded" width="100px" height="36px" />
        </div>
      </div>
    </div>
  );

  switch (variant) {
    case 'post':
      return renderPostSkeleton();
    case 'community':
      return renderCommunitySkeleton();
    case 'user':
      return renderUserSkeleton();
    case 'product':
      return renderProductSkeleton();
    default:
      return renderPostSkeleton();
  }
};

export default SkeletonCard;